/* ===================================================================
   ReactivationPush -- the POST-ACTIVATION reactivation nudge (spec 2026-07-08
   / 07-09). Reactivation is NOT a main setup-checklist row; it is a dismissible
   push the owner meets AFTER activation, once they reach the dashboard. Asking
   the customers they have already earned is the fastest path to a first review.

   WHEN IT SHOWS (all must hold; fully derived from real data -- no fabrication):
     1. The account is ACTIVATED: the first review request has been sent
        (requests step done in useSetupProgress).
     2. Reactivation has NOT already run (the server-confirmed enroll marker is
        absent -- the same signal the checklist derived it from).
     3. The owner has not dismissed this push (per-client localStorage flag).
     4. The underlying signals have settled (never flash mid-load).

   It is a nudge, never a wall: one tap opens the reactivation task; the x
   dismisses it for good on this browser. Reuses the styled rh-setup dashboard-
   nudge classes so it inherits theme + the locked accessibility tokens (bold,
   high-contrast, large tap targets); no hardcoded brand hex.
   =================================================================== */
import { useNavigate } from 'react-router-dom';
import { useMe, useSetupProgress } from '../../hooks';
import { useLocalFlag } from '../../hooks/useLocalFlag';
import '../../styles/overlays.css';

export function ReactivationPush() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const progress = useSetupProgress();
  const [dismissed, setDismissed] = useLocalFlag('reactivation-push-off', me?.client_id);

  if (progress.loading) return null;
  if (dismissed) return null;

  // Activation = first ask sent. Reactivation already done = the enroll marker
  // (reactivation step derives done from it). Both read from the same derived
  // source of truth the checklist uses.
  const activated = progress.steps.find((s) => s.id === 'requests')?.done === true;
  const reactivationDone = progress.steps.find((s) => s.id === 'reactivation')?.done === true;
  if (!activated || reactivationDone) return null;

  return (
    <div className="rh-setup" role="region" aria-label="Reactivate past customers">
      <div className="rh-setup__row">
        <div className="rh-setup__icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
          </svg>
        </div>
        <div className="rh-setup__body">
          <div className="rh-setup__title">Get your first reviews now</div>
          <div className="rh-setup__next">
            Ask the customers you have already earned. Reactivation is the fastest way to
            real reviews in your first week.
          </div>
        </div>
        <div className="rh-setup__tools">
          <button
            className="rh-setup__resume"
            onClick={() => navigate('/onboarding?task=reactivation')}
          >
            Ask past customers
          </button>
          <button
            className="rh-setup__x"
            title="Dismiss"
            aria-label="Dismiss reactivation reminder"
            onClick={() => setDismissed(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReactivationPush;
